 
import {
  matchPath,
  RouteProps,
} from 'react-router-dom';

import { TagsEnum, ProductSaleStatus, ConditionEnum } from '@models';
import { PaymentMethodEnum } from '@newModels';
import { novelties, productIdKey } from '@constants';
import { SourceType } from '@utils/clerk/types';
import {
  generateGoToDetailEvent,
} from '@utils/clerk/events';
import { getPathWithoutLang } from '@utils/i18n';
import * as path from '@legacyUrls';
import * as spaPath from '@urls';

import {
  getAnaliticAppliedFiltersFromStorage,
  getProductReferrerPathnameFromStorage,
} from './common';

// There's the code that could yield to the main thread before calling track events
// https://www.iamtk.co/optimizing-inp-for-a-react-app-and-performance-learnings
async function yieldToMain() {
  // @ts-ignore
  if ('scheduler' in window && 'yield' in window.scheduler) {
    // @ts-ignore
    return await window.scheduler.yield();
  }
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

// @ts-ignore
export const safeGTag: typeof gtag = async (...args) => {
  try {
    await yieldToMain();
    // @ts-ignore
    return gtag(...args);
  } catch (e) {
    console.log(e);
    return null;
  }
};

interface RTBEvent {
  eventType: string,
  [key: string]: string | string[],
}

export const safeRTBHouse = (...events: RTBEvent[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('safeRTBHouse', events);
  } else {
    (window.rtbhEvents = window.rtbhEvents || []).push(
      ...events,
      {
        eventType: 'uid',
        id: window.RTB_HOUSE_USER_ID,
      },
    );
  }
};

export const safeFbq = async (...args: Parameters<typeof fbq>) => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('safeFb', args);
    }

    await yieldToMain();
    return fbq(...args);
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const safeEsputnik = (): typeof esTrackingClient => {
  try {
    if (esTrackingClient) {
      return esTrackingClient;
    } else {
      return {
        sendEvent: () => undefined,
        sendEvent_v2: () => undefined,
      };
    }
  } catch (e) {
    console.log(e);
    return {
      sendEvent: () => undefined,
      sendEvent_v2: () => undefined,
    };
  }
};

const contentGroupRoutes: Record<string, RouteProps | RouteProps[]> = {
  main: { path: path.mainPage, exact: true },
  product: { path: path.productDetails, exact: true },
  category: { path: path.catalog },
  virtual_catalog: { path: spaPath.virtualCatalog },
  tag: { path: path.tag },
  brand: { path: path.catalogBrand },
  member: { path: spaPath.publicMemberProfile },
  favourites: { path: path.favouriteProducts }, // Should be before cabinet
  cabinet: { path: '/my/' },
  search: { path: '/clothes/' },
  collection: [
    { path: spaPath.collections, exact: true },
    { path: spaPath.dayDiscount, exact: true },
    { path: spaPath.newArrivals, exact: true },
  ],
};
export const getContentGroup = (pathname: string) => (
  Object.keys(contentGroupRoutes).find(
    (key) => {
      const routes = contentGroupRoutes[key];

      if (Array.isArray(routes)) {
        return routes.find((route) => (
          matchPath(pathname, route)
        ));
      }
      return matchPath(pathname, routes);
    },
  ) || 'other'
);

export const sendGAActivitiesEvent = (action: string, type: string) => ga(
  'send',
  'event',
  'Activities',
  action,
  type,
);

// Responsible: a.melon?
export const sendCustomerDataEvent = (
  userId: number,
  firstName: string | null | undefined,
  email: string | null | undefined,
  phone: string | null | undefined,
) => {
  try {
    safeEsputnik().sendEvent(
      'CustomerData',
      {
        client_id: `${userId}`,
        firstName: `${firstName || ''}`,
        email: `${email || ''}`,
        phone: `${phone || ''}`,
      },
    );
  } catch (e) {
    console.log(e);
  }
};

// Responsible: b.voloshyn
// Maybe deprecated
const insertTrafmagScript = () => {
  const id = 'trafmag-script';
  if (document.getElementById(id)) { return; }
  const s = document.createElement('script');
  s.async = true;
  s.id = id;
  s.src = 'https://t.trafmag.com/js/clear-shafaua.js';
  const a = document.getElementsByTagName('script')[0];
  a.parentNode!.insertBefore(s, a);
};

// Responsible: b.voloshyn
export const sendRTBHouseHomeEvent = () => {
  safeRTBHouse({
    eventType: 'home',
  });
};

// Responsible: b.voloshyn
export const sendRTBHouseCategoryEvent = (categoryId: string) => {
  safeRTBHouse({
    eventType: 'category',
    categoryId,
  });
};

// Responsible: b.voloshyn
export const sendRTBHouseProductEvent = (productId: string) => {
  safeRTBHouse({
    eventType: 'offer',
    offerId: productId,
  });
};

// Responsible: b.voloshyn
export const sendRTBHouseFavoriteEvent = (productId: string) => {
  safeRTBHouse({
    eventType: 'wishlist',
    offerId: productId,
  });
};

// Responsible: b.voloshyn
export const sendRTBHouseSearchEvent = (productIds: string[]) => {
  safeRTBHouse({
    eventType: 'listing',
    offerIds: productIds,
  });
};

// Responsible: b.voloshyn
export const sendRTBHouseAddToCartEvent = (productId: string) => {
  safeRTBHouse({
    eventType: 'basketadd',
    offerId: productId,
  });
};

// Responsible: l.vladymchuk
export const sendRTBHousePlaceboEvent = () => {
  safeRTBHouse({
    eventType: 'placebo',
  });
};

// Responsible: l.vladymchuk
export const sendRTBHouseCartStatusEvent = (productIds: string[]) => {
  safeRTBHouse({
    eventType: 'basketstatus',
    offerIds: productIds,
  });
};

// Responsible: b.voloshyn
export const sendRTBHousePurchaseEvent = ({
  conversionId,
  conversionValue,
  productIds,
}: {
  conversionId: string,
  conversionValue: string,
  productIds: string[],
}) => {
  safeRTBHouse({
    eventType: 'conversion',
    conversionClass: 'order',
    conversionSubClass: 'purchase',
    conversionId,
    offerIds: productIds,
    conversionValue,
    conversionCurrency: 'UAH',
  });
};

// Responsible: a.melon?
export const sendNewDialogEvent = (productId: number, productPrice: number | null | undefined) => {
  try {
    safeEsputnik().sendEvent_v2('PurchasedItems_v2', {
      OrderNumber: 'Начало диалога',
      Products: [{
        productKey: `${productId}`,
        price: `${productPrice || 0}`,
        quantity: '1',
        currency: 'UAH',
      }],
    });
  } catch (e) {
    console.log(e);
  }
};

// Responsible: b.voloshyn
export const sendDynamicRemarketingEvent = (
  eventName: 'purchase'
  | 'add_to_cart'
  | 'view_item'
  | 'view_item_list'
  | 'view_search_results'
  | 'add_to_wishlist',
  productsIds: number[],
  analyticCommission?: string | null,
) => {
  safeGTag('event', eventName, {
    value: analyticCommission,
    items: productsIds.map((id) => ({
      id,
      google_business_vertical: 'retail',
    })),
    send_to: 'Google_ads',
  });
};

export const sendProductCheckoutThankYouPageOpenEvent = ({
  productId,
  productsIds,
  price,
  buyer,
  contentName,
  threadId,
  analyticCommission,
}: {
  productsIds: number[],
  productId: number,
  price?: number | null,
  buyer?: UserInfo | null,
  contentName?: string | null,
  threadId?: number,
  catalogName?: string | null,
  analyticCommission?: string | null,
}) => {
  try {
    const alreadySent = sessionStorage.getItem(`${productIdKey}${productId}`);
    if (!alreadySent) {
      // Responsible: b.voloshyn
      sendDynamicRemarketingEvent(
        'purchase',
        productsIds,
        analyticCommission,
      );

      sendRTBHousePurchaseEvent({
        conversionId: String(threadId),
        conversionValue: analyticCommission || '0',
        productIds: productsIds.map(String),
      });

      // Responsible: b.voloshyn
      safeFbq('track', 'Purchase', {
        content_name: contentName || '',
        content_category: 'messages',
        content_ids: [String(productId)],
        content_type: 'product',
        value: Number(analyticCommission) || 0,
        currency: 'UAH',
        ...(threadId && {
          event_id: threadId,
        }),
      });

      // Responsible: a.melon?
      safeEsputnik().sendEvent(
        'PurchasedItems',
        {
          OrderNumber: `${threadId || 0}`,
          PurchasedItems: [{
            productKey: `${productId}`,
            quantity: '1',
            price: `${price || 0}`,
          }],
        },
      );

      // Product buy create new dialog
      // Responsible: a.melon?
      sendNewDialogEvent(productId, price);

      if (buyer) {
        sendCustomerDataEvent(
          buyer.id,
          buyer.firstName,
          buyer.email,
          buyer.phone,
        );
      }

      // trafmag event
      // Responsible: b.voloshyn
      insertTrafmagScript();
    }
  } catch (e) {
    //  linter is happy
    console.log(e);
  }
};

export type EcommerceTriggerProduct = {
  id: number
  name: string
  price?: number | null
  brand?: { name: string } | null,
  catalog?: { slug: string } | null,
  ratingAmount?: number | null,
  quantity?: number,
  tags?: TagsEnum[],
  saleLabel?: {
    status: ProductSaleStatus,
  },
};
type EcommerceTriggerEventArg = (
  { products: EcommerceTriggerProduct[] }
  & (
    { event:
    'begin_checkout'
    | 'add_to_cart'
    | 'view_item'
    | 'view_item_list'
    | 'select_item'
    | 'add_to_wishlist'
    }
    | {
      event: 'purchase',
      socialPosts: boolean,
      threadId: number,
      analyticCommission?: string | null,
      sellerType?: 'super-seller' | 'new-seller' | 'regular-seller',
      isPrivateSeller: boolean,
      catalogSlug?: string,
      free_delivery?: 'seller_promoaction',
      characteristics?: {
        id: number,
        choices: {
          id: number,
        }[],
      }[],
      referrer?: string,
    }
  )
);

export const ecommerceTrigger = (arg: EcommerceTriggerEventArg) => {
  try {
    const { event, products } = arg;
    const productReferrerPathname = getProductReferrerPathnameFromStorage(products[0].id);
    const analiticAppliedFilters = productReferrerPathname ? getAnaliticAppliedFiltersFromStorage(productReferrerPathname) : null;
    const pathname = getPathWithoutLang(window.location.pathname);

    const itemListNameUrls: Record<string, RouteProps[]> = {
      home: [
        { path: path.mainPage, exact: true },
      ],
      product: [
        { path: path.productDetails, exact: true },
      ],
      category: [
        { path: path.baseCatalog },
      ],
      'dynamic-collections': [
        { path: spaPath.collections },
        { path: spaPath.dayDiscount },
        { path: spaPath.newArrivals },
      ],
      user_profile: [
        { path: spaPath.mySubscribedBrandsProducts },
        { path: spaPath.mySubscribedUsersProducts },
        { path: spaPath.myClothes },
      ],
      seller_profile: [
        { path: spaPath.publicMemberProfile },
      ],
    };

    // Responsible: a.koriahina
    let referrer;
    if (event === 'add_to_cart') {
      if (matchPath(pathname, spaPath.looksCollection)) {
        referrer = 'looks';
      } else if (matchPath(pathname, path.productDetails)) {
        referrer = 'item';
      }
    }

    // Responsible: @a.illiustrova
    if (event === 'purchase' && arg.referrer) {
      referrer = arg.referrer;
    }

    // Responsible: a.melon
    const isAnyProductInSale = products.some(
      (product) => [
        ProductSaleStatus.SaleStarted,
        ProductSaleStatus.SaleNotStarted,
      ].includes(product.saleLabel?.status || ProductSaleStatus.SaleNotStarted),
    );

    let itemListName;
    if (event === 'view_item_list' || event === 'select_item') {
      itemListName = Object.keys(itemListNameUrls).find(
        (listName) => itemListNameUrls[listName].find(
          (url: RouteProps) => matchPath(pathname, url),
        ),
      );
    }

    let value;
    if (arg.event === 'purchase') {
      value = arg.analyticCommission;
    } else {
      value = products.reduce((acc, p) => acc + (p.price || 0) * (p.quantity || 1), 0);
    }
    safeGTag(
      'event',
      event,
      {
        send_to: 'GA4',
        value,
        ...(arg.event === 'purchase' && {
          reviews: arg.products.map((product) => String(!!product.ratingAmount)),
          ...(!!analiticAppliedFilters && { filters: analiticAppliedFilters }),
          free_delivery_products: arg.products.map(
            (product) => String(product.tags?.includes(TagsEnum.FreeShipping)),
          ),
          sale: isAnyProductInSale ? 'sale' : 'nonsale',
          user_type: arg.sellerType,
          subcat: arg.catalogSlug,
          free_delivery: arg.free_delivery,
          social_reviews: String(arg.socialPosts),
          is_private_seller: String(arg.isPrivateSeller),
        }),
        ...(itemListName ? { item_list_name: itemListName } : {}),
        ...(
          'threadId' in arg
            ? { transaction_id: `${arg.threadId}` }
            : {}
        ),
        ...(referrer ? { referrer } : {}),
        currency: 'UAH',
        items: products.map(
          (p) => ({
            item_id: `${p.id}`,
            item_name: p.name,
            item_brand: p.brand?.name || '',
            item_category: p.catalog?.slug || '',
            price: `${p.price}`,
            currency: 'UAH',
            quantity: p.quantity ? `${p.quantity}` : '1',
          }),
        ),
      },
    );
  } catch (e) {
    console.warn(e);
  }
};

export const loginTrigger = () => {
  safeGTag(
    'event',
    'login',
    { send_to: 'GA4' },
  );

  // Deprecated?
  try {
    window.dataLayer?.push({
      event: 'user_login',
    });
  } catch (e) {
    console.warn(e);
  }
};

export const sendOnLikeEvent = (
  productId: number,
  price: number | null | undefined,
) => {
  sendDynamicRemarketingEvent(
    'add_to_wishlist',
    [productId],
    typeof price === 'number'
      ? String(price)
      : undefined
  );
};

export const sendProductDynamicCollectionClick = (
  collectionSlug: string,
  itemCount: number,
  productId: number,
) => {
  generateGoToDetailEvent(
    itemCount.toString(),
    productId.toString(),
    collectionSlug === novelties
      ? SourceType.personalNewArrivalsBlock
      : SourceType.personalDayDiscountBlock,
  );
};

export const sendProductSuggestionBlockClick = (
  itemCount: number,
  productId: number,
) => {
  generateGoToDetailEvent(
    itemCount.toString(),
    productId.toString(),
    SourceType.personalSuggestionsBlock,
  );
};

export const sendTopSellerProductsBlockClick = (
  itemCount: number,
  productId: number,
) => {
  generateGoToDetailEvent(
    itemCount.toString(),
    productId.toString(),
    SourceType.personalTopSellerProductsBlock,
  );
};

export const sendOpenReviewFormEvent = ({
  role,
  referrer,
  deal,
} : {
  role: 'buyer' | 'seller',
  referrer: 'chat' | 'order_details' | 'review_requests',
  deal: boolean
}) => {
  safeGTag(
    'event',
    `open_review_by_${role}`,
    {
      referrer,
      deal,
      send_to: 'GA4',
    },
  );
};

export const sendSubmitReviewFormEvent = ({
  role,
  deal,
} : {
  role: 'buyer' | 'seller' | 'product',
  deal: boolean
}) => {
  safeGTag(
    'event',
    `leave_review_for_${role}`,
    {
      deal: String(deal),
      send_to: 'GA4',
    },
  );
};

export const sendProductDetailsStartDialogEvent = ({
  reviews,
  namespace,
}: {
  reviews: boolean,
  namespace: string,
}) => {
  safeGTag(
    'event',
    'product_details_start_chat',
    {
      send_to: 'GA4',
      reviews: String(reviews),
      cat: namespace,
    },
  );
};

// Responsible: @a.koriahina
export const sendBuyButtonClickEvent = ({
  referrer,
} : {
  referrer: 'product_details' | 'chat' | 'reviews',
}) => {
  safeGTag(
    'event',
    'buy_button_click',
    {
      send_to: 'GA4',
      referrer,
    },
  );
};

// Responsible: @a.koriahina
export const sendBuyButtonOrderCompletedEvent = ({
  reviews,
  freeDelivery,
  paymentMethod,
  namespaces,
}: {
  reviews: string[],
  freeDelivery: 'first_order' | 'none',
  paymentMethod: PaymentMethodEnum,
  namespaces: string[],
}) => {
  const paymentMethodMap = {
    [PaymentMethodEnum.SafePayment]: 'bo',
    [PaymentMethodEnum.SellerCard]: 'seller_card',
    [PaymentMethodEnum.PayLater]: 'pay_later',
  };

  safeGTag(
    'event',
    'buy_button_order_completed',
    {
      send_to: 'GA4',
      reviews,
      free_delivery: freeDelivery,
      payment_method: paymentMethodMap[paymentMethod],
      cat: namespaces,
    },
  );
};

export const sendItemAddedEvent = ({
  productId,
  scale,
  price,
  recommendedPrice,
  brand,
  condition,
  titleCount,
  descriptionСount,
  photoCount,
}: {
  productId: number,
  scale: string,
  price?: number | null,
  recommendedPrice?: number | null,
  brand?: string | null,
  condition: ConditionEnum,
  titleCount: number,
  descriptionСount: number,
  photoCount: number,
}) => {
  safeGTag(
    'event',
    'item_added',
    {
      product_id: productId,
      product_scale: scale,
      condition,
      title_count: titleCount,
      description_count: descriptionСount,
      photo_count: photoCount,
      ...(price && { price }),
      ...(recommendedPrice && { recommendedPrice }),
      ...(brand && { brand }),
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSearchOpenEvent = () => (
  safeGTag(
    'event',
    'search_open',
    { send_to: 'GA4' },
  )
);

// Responsible: @a.illiustrova
export const sendSearchApplyEvent = ({
  collection,
  namespace,
  categorySlug,
}: {
  collection?: string,
  namespace?: string,
  categorySlug?: string,
}) => (
  safeGTag(
    'event',
    'search_apply',
    {
      send_to: 'GA4',
      collection,
      cat: namespace,
      subcat: categorySlug,
    },
  )
);

// Responsible: @a.illiustrova
export const sendFilterApplyEvent = ({
  collection,
  namespace,
  categorySlug,
  item_characteristics,
  filters,
}: {
  collection?: string,
  namespace?: string,
  categorySlug?: string,
  item_characteristics?: string,
  filters?: string,
}) => (
  safeGTag(
    'event',
    'filter_apply',
    {
      send_to: 'GA4',
      collection,
      cat: namespace,
      subcat: categorySlug,
      ...(!!item_characteristics && { item_characteristics }),
      ...(!!filters && { filters }),
    },
  )
);

// Responsible: @a.koriahina
export const sendPotentialDealEvent = (
  eventName: 'deal_detection_popup_view'
  | 'deal_detection_no'
  | 'deal_detection_yes',
) => (
  safeGTag(
    'event',
    eventName,
    { send_to: 'GA4' },
  )
);

// Responsible: @a.koriahina
export const sendSmartSelectPackageEvent = (
  referrer: 'landing' | 'checkout' | 'chat' | 'settings',
) => (
  safeGTag(
    'event',
    'smart',
    {
      referrer,
      send_to: 'GA4',
    },
  )
);

// Responsible: @a.illiustrova
export const sendFeedFilterApplyEvent = (
  eventName:
  'filter_free_shipping'
  | 'filter_sale'
  | 'filter_made_in_ua',
  referrer?: string,
) => (
  safeGTag(
    'event',
    eventName,
    {
      referrer,
      send_to: 'GA4',
    },
  )
);

// Responsible: @a.illiustrova
export const sendProfileMenuOptionEvent = (
  option: 'goods'
  | 'purchases'
  | 'sales'
  | 'potential_deals'
  | 'subscriptions'
  | 'smart'
  | 'wallet'
  | 'settings'
  | 'reviews'
  | 'my_profile_menu'
  | 'super_seller_page'
  | 'message_to_subscribers',
) => {
  safeGTag(
    'event',
    'profile_click',
    {
      send_to: 'GA4',
      profile_option: option,
    },
  );
};

// Responsible: @a.illiustrova
export const sendAddPromoActionEvent = (
  referrer: 'product_details'
  | 'goods',
) => {
  safeGTag(
    'event',
    'add_promoaction',
    {
      send_to: 'GA4',
      referrer,
    },
  );
};

// Responsible: @a.illiustrova
export const sendApplyPromoActionEvent = (
  referrer: 'product_details'
  | 'goods',
) => {
  safeGTag(
    'event',
    'add_free_delivery_apply',
    {
      send_to: 'GA4',
      referrer,
    },
  );
};

// Responsible: @a.illiustrova
export const sendDeletePromoActionEvent = () => {
  safeGTag(
    'event',
    'delete_free_delivery',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendSellerSaleFunnelEvent = (
  action:
  'take_part'
  | 'view_available'
  | 'add_products',
) => {
  const eventName = `seller_sale_${action}`;

  safeGTag(
    'event',
    eventName,
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendSellerAddProductsFinalEvent = (
  type: 'all' | 'chosen',
) => {
  safeGTag(
    'event',
    'seller_sale_add_products_final',
    {
      type,
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendConfirmedPotentialDealsViewEvent = () => {
  safeGTag(
    'event',
    'view',
    {
      category: 'potential_deals',
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendConfirmedPotentialDealsSubmitEvent = () => {
  safeGTag(
    'event',
    'click_yes',
    {
      category: 'potential_deals',
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendPromotionPurchaseEvent = (
  type: string,
  referrer?: string,
  status?: string,
) => {
  safeGTag(
    'event',
    'promotion_purchase',
    {
      type: type.toLowerCase(),
      referrer,
      status,
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon, @a.illiustrova
export const sendAddToFavoritesEvent = ({
  referrer,
  collection,
} : {
  referrer?: string,
  collection?: string,
}) => {
  safeGTag(
    'event',
    'item_like',
    {
      send_to: 'GA4',
      referrer,
      collection,
    },
  );
};

// Responsible: @a.melon
export const sendCheckoutThanksPageBannerClickEvent = () => {
  safeGTag(
    'event',
    'thankyoupage_order_click_bannerzsu',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSocialFeedSharePostEvent = () => {
  safeGTag(
    'event',
    'share_post',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSocialFeedSubscribeUserEvent = (referrer?: string) => {
  safeGTag(
    'event',
    'subscribe_user',
    {
      send_to: 'GA4',
      referrer,
    },
  );
};

// Responsible: @a.illiustrova
export const sendSocialFeedSavePostEvent = () => {
  safeGTag(
    'event',
    'save_post',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible @a.illiustrova
export const sendSocialFeedLikePostEvent = () => {
  safeGTag(
    'event',
    'product_details_like_tap',  // Name missmatch, because reused old event
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSocialFeedOpenEvent = () => {
  safeGTag(
    'event',
    'open_feed',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSocialFeedOpenPostEvent = () => {
  safeGTag(
    'event',
    'open_post',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendSellerSendMessageEvent = () => {
  safeGTag(
    'event',
    'seller_send_message',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: b.voloshyn
export const sendOpenMyOwnProductEvent = () => {
  safeGTag(
    'event',
    'open_own_product_details',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: b.voloshyn
export const sendProductViewItemEvent = ({
  id,
  name,
  reviews,
  price,
  brandName,
  categorySlug,
  freeShipping,
  socialPosts,
  ownerType,
  isPrivateSeller,
  filters,
  referrer,
  scale,
}: {
  id: string,
  name: string,
  reviews: boolean,
  price: number,
  brandName?: string,
  categorySlug: string,
  freeShipping: boolean,
  socialPosts: boolean,
  ownerType: 'super-seller' | 'new-seller' | 'regular-seller',
  isPrivateSeller: boolean,
  filters?: string,
  referrer?: string,
  scale: string,
}) => {
  safeGTag(
    'event',
    'view_item',
    {
      send_to: 'GA4',
      reviews: String(reviews),
      value: price,
      currency: 'UAH',
      items: [
        {
          item_name: name,
          item_id: id,
          item_brand: brandName,
          item_category: categorySlug,
          price,
          curency: 'UAH',
          quantity: '1',
        },
      ],
      free_delivery_products: String(freeShipping),
      user_type: ownerType,
      is_private_seller: String(isPrivateSeller),
      subcat: categorySlug,
      social_reviews: String(socialPosts),
      product_scale: scale,
      ...(!!filters && { filters }),
      ...(!!referrer && { referrer }),
    },
  );

  sendDynamicRemarketingEvent('view_item', [Number(id)], String(price));
};

// Responsible: b.voloshyn
export const sendProductPageViewEvent = ({
  id,
  price,
}: {
  id: string,
  price: number,
}) => {
  safeGTag('event', 'page_view', {
    'send_to': 'Google_ads',
    'dynx_itemid': id,
    'dynx_pagetype': 'offerdetail',
    'dynx_totalvalue': price,
    'ecomm_prodid': id,
    'ecomm_pagetype': 'product',
    'ecomm_totalvalue': price,
  });
};

// Responsible: @a.illiustrova
export const sendSimilarProductsViewEvent = () => {
  safeGTag(
    'event',
    'item_similar_page',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendProductSocialPostViewEvent = ({
  referrer,
}: {
  referrer?: string,
}) => {
  safeGTag(
    'event',
    'view_post',
    {
      referrer,
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendRegistrationViewEvent = () => {
  safeGTag(
    'event',
    'registration_page',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova + b.voloshyn
export const sendRegistrationSuccessEvent = () => {
  safeGTag(
    'event',
    'conversion',
    { 'send_to': 'AW-695307191/w37YCO7HxdECELeXxssC' },
  );
  safeGTag(
    'event',
    'conversion',
    { 'send_to': 'AW-827914930/M0pGCPHjprgBELL144oD' },
  );

  safeGTag(
    'event',
    'register_profile',
    {
      send_to: 'GA4',
    },
  );
  // Deprecated?
  // @ts-ignore
  safeGTag({
    'event': 'TrackEvent',
    'eventCategory': 'registration',
    'eventAction': 'done',
    'cpa': 'null',
  });
};

// Responsible: @a.melon
export const sendSwapCheckoutEvent = () => {
  safeGTag(
    'event',
    'swap_checkout',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendSwapCheckoutCompletedEvent = () => {
  safeGTag(
    'event',
    'swap_checkout_completed',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendSwapItemAddedEvent = () => {
  safeGTag(
    'event',
    'swap_item_added',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.melon
export const sendSellerSubscribeEvent = ({
  referrer,
}: {
  referrer: 'category' | 'user_profile' | 'thank_you_page_order' | 'thank_you_page_dialog' | 'item',
}) => {
  safeGTag(
    'event',
    'product_details_subscribe_on_seller',
    {
      referrer,
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendAdsEvent = ({
  eventType,
  promotionType,
}: {
  eventType: 'show_promoted_products' | 'view_promoted_products' | 'purchase_promoted_products',
  promotionType: 'top'  // Only top for now
}) => {
  safeGTag(
    'event',
    eventType,
    {
      send_to: 'GA4',
      type: promotionType,
    },
  );
};
// Responsible: @a.illiustrova
export const sendFilterBrandChoiceEvent = ({
  referrer,
  brandName,
}: {
  referrer: 'popular_brands' | 'filter_feed' | 'brand_hub' | 'brand_page' | 'brand_product_details',
  brandName: string,
}) => {
  safeGTag(
    'event',
    'filter_brand_choice',
    {
      referrer,
      brandName,
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendCatalogIntentEvent = () => {
  safeGTag(
    'event',
    'catalog_intent',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSeoIntentEvent = () => {
  safeGTag(
    'event',
    'seo_intent',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @a.illiustrova
export const sendSellerShopIntentEvent = () => {
  safeGTag(
    'event',
    'seller_shop_intent',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible: @Netpeak
export const sendOutletPurchaseConversionEvent = ({
  transactionId,
  value,
  email,
  phone,
}: {
  transactionId: number,
  value?: string | null,
  email?: string | null,
  phone?: string | null,
}) => {
  safeGTag(
    'event',
    'conversion',
    {
      'send_to': 'AW-16614574657/8pr3CLC-mKkaEMGUufI9',
      'value': value,
      'currency': 'UAH',
      'transaction_id': transactionId,
      ...(email && { email }),
      ...(phone && { phone }),
    },
  );
};

// Responsible: @Netpeak
export const sendOutletRegisterConversionEvent = ({
  email,
  phone,
}: {
  email?: string | null,
  phone?: string | null,
}) => {
  safeGTag(
    'event',
    'conversion',
    {
      'send_to': 'AW-16614574657/aG9wCPzUmKkaEMGUufI9',
      'value': 10,
      'currency': 'UAH',
      ...(email && { email }),
      ...(phone && { phone }),
    },
  );
};

// Responsible: @a.illiustrova
export const sendCreateUserBrandEvent = () => {
  safeGTag(
    'event',
    'add_item_s1_add_brand',
    {
      send_to: 'GA4',
    },
  );
};

// Responsible @a.koriahina
// When user sees EncourageSendMessage block
export const sendShowEncourageSendMessageEvent = () => {
  safeGTag(
    'event',
    'smart_landing_shown',  // Name missmatch, because reused old event
    {
      send_to: 'GA4',
    },
  );
};

// Responsible @a.koriahina
// When user clicks on "Send" button on thank you page
// in EncourageSendMessage block
export const sendEncouragedSendMessageEvent = () => {
  safeGTag(
    'event',
    'autorenew_off',  // Name missmatch, because reused old event
    {
      send_to: 'GA4',
    },
  );
};

// Responsible @a.plaksin
export const sendOutletStartDialog = () => {
  safeGTag(
    'event',
    'started_dialog_new',
    {
      send_to: 'GA4',
    },
  );
};
